import { Link, Stack, Text } from '@fluentui/react';
import { TSettingsCategory } from '../components/Dashboard';
import { useState } from 'react';
import { ApprovalGridConfig } from './components/ApprovalGridConfig';
import { ManagerApprovalConfig } from './components/ApprovalsConfig/ManagerApprovalConfig';
import { LanguageGridConfig } from './components/LanguageGridConfig';
import { TimeAndCalendarGridConfig } from './components/TimeAndCalendarGridConfig';
import { AllocationsConfigView } from './components/TimesheetConfig/AllocationsConfigView';
import { AdminProjects } from './components/AdminProjects/AdminProjects';
import { AppAdminCalendarConfig } from './components/AppAdminCalendarConfig/AppAdminCalendarConfig';
import { AdminTimeConfig } from './components/TimeConfig/TimeConfig';
import { ValidationConfig } from './components/ValidationConfig';
import { PowerHubSettings } from './components/PowerHubSettings/PowerHubSettings';
import { JiraUploadSettings } from './components/TimeEntries/TimeEnitriesUpload';
import { HelpConfig } from './components/HelpConfig/HelpConfig';
import { SystemInformation } from './components/SystemInformation/SystemInformation';
import { PeriodConfig } from './components/PeriodConfig/PeriodConfig';
import { IntegrationsV2 } from './components/integrationsv2/IntegrationsV2';
import { ResourceOverview } from './components/ResourceOverview/ResourceOverview';
import { TimeEntryCorrections } from '../../_pages/TimeEntryCorrections/TimeEntryCorrections';

export const configurationConfig: TSettingsCategory = {
    iconName: 'Settings',
    link: '/',
    title: 'Modern Settings',
    shortDescription: '',
    description: (
        <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
            <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                Welcome to Modern Settings, this page will over time be the new ways of workings with Settings in Time for Teams
            </Text>
        </Stack>
    ),
    children: [
        {
            iconName: 'PlugDisconnected',
            link: 'integrations',
            title: 'Integrations',
            shortDescription:
                'The Integration section allows for seamless connections between our platform and external applications, enhancing collaboration.',
            description: function Description() {
                const [expanded, setExpanded] = useState(false);
                return (
                    <Stack horizontalAlign="center">
                        <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                            The Integration features are designed to create seamless connections between our platform and external applications.
                        </Text>
                        <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                            This feature enables users to effortlessly exchange data, and collaborate across multiple platforms, making it easier than ever to
                            manage resources effectively. {!expanded && <Link onClick={() => setExpanded(e => !e)}>See {expanded ? 'less..' : 'more..'}</Link>}
                        </Text>
                        {expanded && (
                            <>
                                <br />
                                <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                                    By integrating with other applications, our platform can harness its unique functionalities and data to provide a more
                                    comprehensive and tailored resource management experience.
                                </Text>
                                <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                                    This not only improves overall efficiency but also eliminates the need to manually transfer data or switch between multiple
                                    systems. <Link onClick={() => setExpanded(e => !e)}>See {expanded ? 'less..' : 'more..'}</Link>
                                </Text>
                            </>
                        )}
                    </Stack>
                );
            },
            children: <IntegrationsV2 />,
        },
        {
            title: 'Approval Settings',
            link: 'approval-settings',
            shortDescription: 'Control how submitted timesheets are reviewed and approved by managers and project leads.',
            description: (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                    <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                        Approval Settings control how time registrations move from submission to approval. Manager Approval handles the period-level workflow.
                        Project Approval handles per-entry review, where project managers approve hours registered on their projects.
                    </Text>
                </Stack>
            ),
            iconName: 'InboxCheck',
            children: [
                {
                    title: 'Manager Approval',
                    link: 'manager-approval',
                    shortDescription: 'Manage how people managers handle submitted timesheets and period approvals.',
                    description: (
                        <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                            <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                                Manager Approval covers the standard flow where users submit their reporting period for approval by their manager. Use these
                                settings to control how approvers handle submitted timesheets and closed periods.
                            </Text>
                        </Stack>
                    ),
                    iconName: 'Contact',
                    children: <ManagerApprovalConfig />,
                },
                {
                    title: 'Project Approval',
                    link: 'project-approval',
                    shortDescription: 'Let project managers review and approve hours registered on their projects.',
                    description: (
                        <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                            <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                                Project Approval lets project managers approve hours registered on their projects, entry by entry. Use these settings to enable
                                the view and configure how rates apply.
                            </Text>
                        </Stack>
                    ),
                    iconName: 'ProjectCollection',
                    children: <ApprovalGridConfig />,
                },
            ],
        } as TSettingsCategory,
        {
            title: 'Language (preview)',
            link: 'language',
            shortDescription: 'The language section allows you to set the default language of the app.',
            description: (
                <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                    The language section allows you to set the default language of the app.
                </Text>
            ),
            iconName: 'LocaleLanguage',
            children: <LanguageGridConfig />,
        } as TSettingsCategory,
        {
            iconName: 'AlarmClock',
            link: 'time-calendar',
            title: 'Time and Calendar',
            shortDescription:
                'This area contains the possibility to do changes in time and calendar specific settings such as Periods, Capacity, Work days and Date/Time formats',
            description: (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                    <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                        Configure time and calendar settings including default daily capacity, work days, date formats and more. These settings affect how time
                        entries are handled and displayed throughout the application.
                    </Text>
                </Stack>
            ),
            children: [
                {
                    link: 'work-days-capacity',
                    title: 'Work Days & Capacity',
                    shortDescription: 'Set up how the calendar interacts with the timesheet view.',
                    description: (
                        <Stack horizontalAlign="center">
                            <Text variant="mediumPlus">
                                Customize your work schedule by selecting designated work days and defining the daily capacity for the time registrants.
                            </Text>
                            <Text variant="mediumPlus">This helps in accurately tracking work hours and ensuring balanced workload distribution</Text>
                        </Stack>
                    ),
                    iconName: 'EventDate',
                    children: <TimeAndCalendarGridConfig />,
                },
                {
                    link: 'periods',
                    title: 'Periods',
                    shortDescription: 'Maintain periods for time entries',
                    description: (
                        <Stack horizontalAlign="center">
                            <Text variant="mediumPlus">
                                Configure the timing of entries to suit the organization's needs by managing the timing of the periods in which the users can
                                register time in.
                            </Text>
                            <Text variant="mediumPlus">
                                From here you can set the state of each period, allowing you to control which periods are available for timesheet entries.
                            </Text>
                            <Text variant="mediumPlus">
                                This ensures that periods are accurately tracked and managed according to your specific requirements
                            </Text>
                        </Stack>
                    ),
                    iconName: 'Calendar',
                    children: <PeriodConfig />,
                },

                {
                    iconName: 'Calendar',
                    link: 'calendar',
                    title: 'Calendar Configuration',
                    shortDescription: 'Calendar Configuration',
                    description: (
                        <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                            <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                                Calendar Configuration
                            </Text>
                        </Stack>
                    ),
                    children: <AppAdminCalendarConfig />,
                },
            ],
        } as TSettingsCategory,
        {
            iconName: 'TimeEntry',
            link: 'timesheet',
            title: 'Timesheet',
            shortDescription: 'This area lets you configure the timesheet view.',
            description: (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                    <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                        Configure the timesheet view to suit your needs. This includes settings for features which are directly related to the timesheet view.
                    </Text>
                </Stack>
            ),
            children: [
                {
                    iconName: 'ReminderGroup',
                    link: 'allocation',
                    title: 'Allocations',
                    shortDescription: 'Set up how allocations interact with the timesheet view- and submission.',
                    description: (
                        <Stack horizontalAlign="center">
                            <Text variant="mediumPlus">
                                Allocations are the hours a user is assigned to work on a project. This section allows you to configure how allocations are
                                displayed.
                            </Text>
                            <Text variant="mediumPlus"></Text>
                        </Stack>
                    ),
                    children: <AllocationsConfigView />,
                },
            ],
        },
        {
            iconName: 'ProjectCollection',
            link: 'admin-projects',
            title: 'Administrative Projects',
            shortDescription: 'Administrative Projects',
            description: (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                    <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                        Administrative Projects
                    </Text>
                </Stack>
            ),
            children: <AdminProjects />,
        },
        {
            iconName: 'CalendarSettings',
            link: 'time',
            title: 'Time configuration',
            shortDescription: 'Time configuration',
            description: (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                    <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                        Time configuration
                    </Text>
                </Stack>
            ),
            children: <AdminTimeConfig />,
        },
        {
            iconName: 'Admin',
            link: 'validate',
            title: 'Validation configuration',
            shortDescription: 'Validation configuration',
            description: (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                    <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                        Validation configuration
                    </Text>
                </Stack>
            ),
            children: <ValidationConfig />,
        },
        {
            iconName: 'PlugConnected',
            link: 'powerhub',
            title: 'PowerHub Settings',
            shortDescription: 'PowerHub Settings',
            description: (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                    <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                        PowerHub Settings
                    </Text>
                </Stack>
            ),
            children: <PowerHubSettings />,
        },
        {
            iconName: 'Upload',
            link: 'jiraupload',
            title: 'Time Entries Import',
            shortDescription: 'Time Entries Import',
            description: (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                    <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                        Time Entries Import
                    </Text>
                </Stack>
            ),
            children: <JiraUploadSettings />,
        },
        {
            iconName: 'HandsFree',
            link: 'help',
            title: 'Help configuration',
            shortDescription: 'Help configuration',
            description: (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                    <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                        Help configuration
                    </Text>
                </Stack>
            ),
            children: <HelpConfig />,
        },
        {
            iconName: 'Info',
            link: 'system-information',
            title: 'System Information',
            shortDescription: 'System Information',
            description: (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                    <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                        System Information
                    </Text>
                </Stack>
            ),
            children: <SystemInformation />,
        },
        {
            iconName: 'TemporaryUser',
            link: 'resource-overview',
            title: 'Resource Overview',
            shortDescription: 'Resource Overview',
            description: (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                    <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                        Resource Overview
                    </Text>
                </Stack>
            ),
            children: <ResourceOverview />,
            adminOnly: false,
        },
        {
            iconName: 'EditNote',
            link: 'time-entry-corrections',
            title: 'Time Entry Corrections (Preview)',
            shortDescription: 'Manage and review time entry corrections for project managers',
            description: (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 10, maxWidth: 1000 }}>
                    <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                        Review and manage corrections to time entries.
                    </Text>
                    <Text variant="mediumPlus" styles={{ root: { textAlign: 'center' } }}>
                        This administrative tool provides oversight and control over time entry modifications.
                    </Text>
                </Stack>
            ),
            children: <TimeEntryCorrections />,
        },
    ],
};
